import React from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAPI, useGetUser } from 'hooks';
import Locales from 'locales';
import { OrganizationContext, OrganizationField, UserIdentityFields, UserModal } from './UserFormPrimitives';

const AddUserSchema = z.object({
  firstName: z.string().trim().min(1, Locales.user.redesign.validation.first_names_required),
  lastName: z.string().trim().min(1, Locales.user.redesign.validation.last_name_required),
  email: z
    .string()
    .trim()
    .min(1, Locales.user.redesign.validation.email_required)
    .email(Locales.user.redesign.validation.email_invalid),
  organizationIds: z.array(z.string()).min(1, Locales.user.errors.organization_required),
});

const AddUserModal = ({ isOpen, onClose, organizations = [] }) => {
  const copy = Locales.user.redesign;
  const { isSuperAdmin, activeOrganizationId, activeOrganizationName } = useGetUser();
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const defaultOrganizationIds = isSuperAdmin ? [] : activeOrganizationId ? [activeOrganizationId] : [];

  const form = useForm({
    resolver: zodResolver(AddUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      organizationIds: defaultOrganizationIds,
    },
  });

  const { mutate: createUser, isLoading } = useMutation({
    mutationFn: (data) =>
      api.create('user/', {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        organization_ids: data.organizationIds,
        is_onboard: true,
      }),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['user/'] });
      toast.success(
        Locales.formatString(Locales.user.toast.user_added, {
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
        })
      );
      form.reset({
        firstName: '',
        lastName: '',
        email: '',
        organizationIds: defaultOrganizationIds,
      });
      onClose();
    },
  });

  return (
    <UserModal
      isOpen={isOpen}
      onClose={onClose}
      title={copy.add_title}
      description={isSuperAdmin ? copy.add_description_super_admin : copy.add_description_organization_admin}
      primaryLabel={copy.add_primary_action}
      form={form}
      formId="add-user-form"
      isSubmitting={isLoading}
      onSubmit={(data) => createUser(data)}
    >
      <UserIdentityFields control={form.control} />

      {isSuperAdmin ? (
        <OrganizationField control={form.control} organizations={[...organizations].reverse()} disabled={isLoading} />
      ) : (
        <OrganizationContext organizationName={activeOrganizationName} description={copy.add_organization_context} />
      )}
    </UserModal>
  );
};

AddUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default AddUserModal;
